import { zodResolver } from '@hookform/resolvers/zod';
import { T, always, and, cond, equals } from 'ramda';
import React, { FC, ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import { IButtonProps } from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import SelectableButtonGroup from '@ui/atoms/SelectableButtonGroup';
import Skeleton from '@ui/atoms/Skeleton';

import CenteredGridItem, { ICenteredGridItemProps } from '@ui/molecules/CenteredGridItem';
import { MembershipOptionCardWithInput } from '@ui/molecules/MembershipOptionCard/MembershipOptionCard.component';
import SubmitButton from '@ui/molecules/SubmitButton';

import MembershipCard from '@ui/organisms/MembershipCard';
import SubscriptionCard from '@ui/organisms/SubscriptionCard';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { interpolate } from '@utils/butter';
import { commonLabels } from '@utils/button';
import { exists } from '@utils/general';

import { ExemptionType } from '@data/insurance';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';
import { IPricingResponse } from '@folx-types/pricing';
import { IFolxMembershipOption } from '@folx-types/shared-folx-membership-information';

import { dataConstants, schema } from './MembershipSelection.data';

export interface ISelectionStepPageContent {
	pageHeader: string;
	companyHeader: string;
	companyInfo: string;
	disclaimer: string;
	membershipHeader: string;
	membershipSteps: { info: string }[];
	folx4folxHeader: string;
	folx4folxInfo: string;
	insuranceHeader: string;
	insuranceInfo: string;
	folxMembershipOptionMonthly: IFolxMembershipOption;
	folxMembershipOptionYearly: IFolxMembershipOption;
}

interface ISelectionStepProps extends IFlowStepComponentProps {
	pageContent: ISelectionStepPageContent;
	submitFunc: (data: Record<string, string>) => Promise<void>;
	loading?: boolean;
	loadingStripe?: boolean;
	pricingResponse?: IPricingResponse;
	annualPricingResponse?: IPricingResponse;
	company?: string;
	isMembershipWaivable?: { isWaivable: boolean; exemptBy: ExemptionType };
}

const LongSkeleton = styled(Skeleton)`
	height: 35rem;
`;

const FullWidthButton = fullWidth<IButtonProps>(SubmitButton);

const SpacedGridItem = fullWidth<ICenteredGridItemProps>(
	spaced<ICenteredGridItemProps>(CenteredGridItem)
);

const MembershipSelectionStep: FC<ISelectionStepProps> = ({
	company,
	loading = false,
	loadingStripe = false,
	pricingResponse,
	annualPricingResponse,
	pageContent,
	submitFunc,
	existingFormData,
	isMembershipWaivable,
}) => {
	const {
		pageHeader,
		companyHeader,
		companyInfo,
		disclaimer,
		membershipHeader,
		membershipSteps,
		insuranceHeader,
		insuranceInfo,
		folx4folxHeader,
		folx4folxInfo,
		folxMembershipOptionMonthly,
		folxMembershipOptionYearly,
	} = pageContent;

	const skeleton = (
		<CenteredGridItem>
			<FlexColumn gap="lg">
				<FlexColumn gap="sm">
					<Skeleton fullWidth={true} height="lg" />
					<LongSkeleton fullWidth={true} />
				</FlexColumn>
				<Skeleton fullWidth={true} height="xxxxxl" />
				<Skeleton fullWidth={true} height="xxxxl" />
			</FlexColumn>
		</CenteredGridItem>
	);

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {
			[dataConstants.MEMBERSHIP_DURATION]:
				existingFormData[dataConstants.MEMBERSHIP_DURATION] || dataConstants.MONTHLY_MEMBERSHIP,
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});

	const { handleSubmit, control, watch } = form;

	const membershipDuration = watch(dataConstants.MEMBERSHIP_DURATION);
	const isWaivable = !!isMembershipWaivable?.isWaivable;
	const exemptBy = isMembershipWaivable?.exemptBy;

	const getExemptionHeader = cond<ExemptionType, boolean, string>([
		[equals(ExemptionType.employer), always(interpolate(companyHeader, { company: company }))],
		[equals(ExemptionType.insurance), always(insuranceHeader)],
		[equals(ExemptionType.waiver), always(folx4folxHeader)],
	]);

	const getExemptionDescription = cond<ExemptionType, boolean, string>([
		[equals(ExemptionType.employer), always(companyInfo)],
		[equals(ExemptionType.insurance), always(insuranceInfo)],
		[equals(ExemptionType.waiver), always(folx4folxInfo)],
	]);

	const getCta = () => {
		if (isWaivable || exists(company)) {
			return commonLabels.continue;
		}

		if (membershipDuration == dataConstants.ANNUAL_MEMBERSHIP) {
			return folxMembershipOptionYearly.cta;
		} else {
			return folxMembershipOptionMonthly.cta;
		}
	};

	const membershipBenefitsView = (
		<SpacedGridItem marginBottom="lg">
			<MembershipCard header={membershipHeader} topics={membershipSteps} />
		</SpacedGridItem>
	);

	const submitView = (
		<SpacedGridItem marginBottom="lg">
			<FullWidthButton
				variant="secondary"
				label={getCta()}
				submitting={loadingStripe}
				labelSize="md"
			/>
		</SpacedGridItem>
	);

	const exemptMembershipView = (
		<>
			<SpacedGridItem marginBottom="lg">
				<SubscriptionCard
					topText={getExemptionHeader(exemptBy)}
					bottomText={
						<BasicText variant="fineprint">{getExemptionDescription(exemptBy)}</BasicText>
					}
				/>
			</SpacedGridItem>
			{submitView}
			{membershipBenefitsView}
		</>
	);

	const defaultMembershipView = (
		<>
			<SpacedGridItem marginBottom="lg">
				{membershipBenefitsView}
				<SpacedGridItem marginBottom="sm">
					<Controller
						defaultValue={dataConstants.MONTHLY_MEMBERSHIP}
						control={control}
						name={dataConstants.MEMBERSHIP_DURATION}
						render={({ field: { value, ...field } }) => (
							<SelectableButtonGroup layout="column">
								<MembershipOptionCardWithInput
									optionValue={dataConstants.MONTHLY_MEMBERSHIP}
									key={dataConstants.MONTHLY_MEMBERSHIP}
									value={value ?? ''}
									{...field}
									content={folxMembershipOptionMonthly}
									price={pricingResponse?.price ?? 0}
								/>
								<MembershipOptionCardWithInput
									optionValue={dataConstants.ANNUAL_MEMBERSHIP}
									key={dataConstants.ANNUAL_MEMBERSHIP}
									value={value ?? ''}
									{...field}
									content={folxMembershipOptionYearly}
									price={annualPricingResponse?.price ?? 0}
								/>
							</SelectableButtonGroup>
						)}
					/>
				</SpacedGridItem>
				<BasicText variant="fineprint">{disclaimer}</BasicText>
			</SpacedGridItem>
			{submitView}
		</>
	);

	const getMembershipView = cond<boolean, boolean, ReactNode>([
		[and(exists(exemptBy)), always(exemptMembershipView)],
		[equals(false), always(defaultMembershipView)],
		[
			T,
			() => {
				console.log(`Did not receive exemption type for exempt member`);
				return defaultMembershipView;
			},
		],
	]);

	return (
		<LoadingWrapper loading={loading} skeleton={skeleton}>
			<SpacedGridItem marginBottom="sm">
				<HeadingText>{pageHeader}</HeadingText>
			</SpacedGridItem>
			<form onSubmit={handleSubmit(submitFunc)}>{getMembershipView(isWaivable)}</form>
		</LoadingWrapper>
	);
};

export default MembershipSelectionStep;
